/**
 * The draft storage key, and which conversation row a stored draft marks.
 *
 * A composer's unsent text lives under `channel_draft_<draftKey>` in `localStorage`, and
 * a draft key is either a channel's `cid` or a thread's compound `<cid>#reply:<id>`.
 * Three modules read that format — the composer's persistence hook, the crash screen's
 * wipe, and the inbox's "this row holds a draft" index — so it is stated once here
 * rather than as three `startsWith` calls that drift apart.
 *
 * Everything here is a calculation: no storage, no React, no clock. That is what lets
 * the one rule the draft mark turns on be tested in the webapp's `node` test
 * environment, the same reason `inbox/_libs/message-preview.ts` names an icon KEY
 * instead of importing a component.
 *
 * ## Every composer persists; only some mark a row
 *
 * `channelDraftStorageKey` is total — an internal note and a thread reply keep their
 * drafts exactly like a message does, and restore them on the way back. What narrows is
 * `cidOfChannelDraftStorageKey`, the question the INDEX asks: which conversation row
 * does this draft belong on? For a note or a reply the answer is none, so they are
 * stored and never marked.
 *
 * ## What marks a row
 *
 * A **thread reply** does not. It is reachable only by reopening that thread, and the
 * conversation row is not where that is surfaced — so a mark there would point at
 * something the row cannot get you to.
 *
 * An **internal note** (`order_comment`) does not. The notes thread lives on the order
 * detail page and is never an inbox row at all, so its cid could only ever match
 * nothing.
 *
 * Everything else does — including a home's **support** conversation: `order_comment`
 * is the comment kind and everything else is the message kind. Stating the rule as an
 * EXCLUSION rather than an allow-list defaults to the message kind, so a channel type
 * that becomes an inbox row later is marked by default instead of silently missing its
 * pencil.
 *
 * ## How a thread key is recognised
 *
 * A `cid` is `<channelTypeId>:<entityId>` (see `channel-cid.ts`): the type is a
 * snake_case slug from a fixed seeded list, and the entity id is a 24-char nanoid over
 * `[0-9a-z]`. Neither half can contain `#`, so a key carrying one is unambiguously a
 * thread's compound key — which is exactly what makes it rejectable by inspection.
 */

import { CHANNEL_TYPE_ORDER_COMMENT, parseChannelCid } from "./channel-cid";

/**
 * Every draft key starts with this — also the wipe's filter.
 *
 * The VALUE is load-bearing: changing it orphans every draft already in a browser.
 */
export const CHANNEL_DRAFT_KEY_PREFIX = "channel_draft_";

/** Separates a channel's own draft from a reply half-written in one of its threads. */
const THREAD_DRAFT_SEPARATOR = "#";

/**
 * Where the draft for `draftKey` (a cid, or a thread's `<cid>#reply:<id>`) is stored.
 *
 * Total on purpose: every composer persists, whatever it posts into.
 */
export function channelDraftStorageKey(draftKey: string): string {
  return `${CHANNEL_DRAFT_KEY_PREFIX}${draftKey}`;
}

/** Whether a `localStorage` key is one of ours. */
export function isChannelDraftStorageKey(key: string): boolean {
  return key.startsWith(CHANNEL_DRAFT_KEY_PREFIX);
}

/**
 * The conversation row a stored draft marks, or `null` when it marks none.
 *
 * `null` — never `""` — for a foreign, malformed, or unmarked key, so a caller can never
 * index a row by the empty string. See "What marks a row" above for the three answers
 * that are `null` and why.
 */
export function cidOfChannelDraftStorageKey(key: string): string | null {
  if (!isChannelDraftStorageKey(key)) return null;

  const draftKey = key.slice(CHANNEL_DRAFT_KEY_PREFIX.length);
  if (draftKey.includes(THREAD_DRAFT_SEPARATOR)) return null;

  // `parseChannelCid` rejects anything that is not `<type>:<id>` with both halves
  // non-empty, which is also what covers the empty-cid key `channel_draft_`.
  const parsed = parseChannelCid(draftKey);
  if (!parsed || parsed.channelTypeId === CHANNEL_TYPE_ORDER_COMMENT)
    return null;

  return draftKey;
}

/**
 * The conversations marked by a set of stored keys.
 *
 * Keys that mark no row are dropped, so this is also what keeps a draft left by an
 * earlier build — a thread reply, an internal note — from marking a row on load.
 */
export function collectCidsWithDraft(
  storageKeys: Iterable<string>,
): ReadonlySet<string> {
  const cids = new Set<string>();
  for (const key of storageKeys) {
    const cid = cidOfChannelDraftStorageKey(key);
    if (cid) cids.add(cid);
  }
  return cids;
}
